import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FileSpreadsheet, AlertTriangle, Download, Loader2, Undo2, RefreshCw, Check,
  Warehouse, ChevronDown, Ban, CircleSlash, ArrowUp, ArrowDown, ChevronsUpDown,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import {
  pendingInvoices, runInvoiceExport, invoiceBatches, downloadInvoiceBatch,
  undoInvoiceBatch, getJob, listTrucks, setBillingStateBulk,
} from "../lib/api";
import JobDetailSheet from "../components/JobDetailSheet";
import { useAuth } from "../lib/auth";
import { can } from "../lib/roles";
import { tenantLocale, tenantTz } from "../lib/company";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

// completed_at is stored in UTC. Showing its first ten characters put every
// NZ job finished before 1pm local on the previous day — the same mistake the
// exported invoice date made.
const localDay = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(tenantLocale(), { timeZone: tenantTz() });
  } catch {
    return String(iso).slice(0, 10);
  }
};

const FORMATS = [
  { key: "xero", label: "Xero", hint: "CSV — imports as draft invoices" },
  { key: "myob", label: "MYOB AccountRight", hint: "Tab-separated — desktop/hybrid only" },
];

// Which column each heading sorts on, and whether it reads as text, a date or
// money. Oldest-first is the useful default for dates; biggest-first for money.
const COLUMNS = [
  { key: "contact", label: "Invoice to", type: "text" },
  { key: "rego", label: "Rego", type: "text" },
  { key: "reference", label: "Their ref", type: "text" },
  { key: "completed_at", label: "Date", type: "date" },
  { key: "total_ex_tax", label: "Amount", type: "number", align: "right" },
];

function sortRows(rows, { key, dir }) {
  const col = COLUMNS.find((c) => c.key === key);
  if (!col) return rows;
  const flip = dir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = a[key], bv = b[key];
    if (col.type === "number") return ((av || 0) - (bv || 0)) * flip;
    if (col.type === "date") {
      return ((av ? Date.parse(av) : 0) - (bv ? Date.parse(bv) : 0)) * flip;
    }
    return String(av || "").localeCompare(String(bv || ""), undefined,
      { sensitivity: "base", numeric: true }) * flip;
  });
}

function SortableHeaders({ sort, onSort, lastLabel, master }) {
  return (
    <tr>
      <th className="px-3 py-2 w-12">{master}</th>
      {COLUMNS.map((c) => {
        const active = sort.key === c.key;
        const Icon = !active ? ChevronsUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
        return (
          <th
            key={c.key}
            className={`px-2 py-2 font-bold ${c.align === "right" ? "text-right" : "text-left"}`}
          >
            <button
              type="button"
              onClick={() => onSort(c.key)}
              title={`Sort by ${c.label.toLowerCase()}`}
              className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 transition-colors ${
                active
                  ? "border-blue-400 dark:border-blue-600 bg-blue-50 dark:bg-blue-950/40 text-blue-800 dark:text-blue-200"
                  : "border-slate-200 dark:border-slate-700 hover:border-slate-400 dark:hover:border-slate-500 hover:bg-white dark:hover:bg-slate-800 hover:text-slate-800 dark:hover:text-slate-100"
              }`}
              data-testid={`invoicing-sort-${c.key}`}
            >
              {c.label}
              <Icon className={`h-3.5 w-3.5 ${active ? "" : "opacity-40"}`} />
            </button>
          </th>
        );
      })}
      <th className="text-left px-2 py-2 font-bold">{lastLabel}</th>
    </tr>
  );
}

function saveFile(name, content) {
  const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function JobRow({ row, checked, onToggle, selectable, onOpen }) {
  return (
    <tr
      className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/40 cursor-pointer"
      onClick={() => onOpen(row.id)}
      title="Open the full job details"
      data-testid={`invoice-row-${row.id}`}
    >
      {/* The whole cell is the hit area — a 16px box is no use on a tablet in
          a workshop. Clicks that land ON the box are left to Radix: handling
          them here too toggled twice and cancelled itself out. */}
      <td
        className="px-3 py-2"
        onClick={(e) => {
          e.stopPropagation();
          if (selectable && !e.target.closest('[role="checkbox"]')) onToggle(row.id);
        }}
      >
        {selectable && (
          <Checkbox
            checked={checked}
            onCheckedChange={() => onToggle(row.id)}
            className="h-5 w-5"
            data-testid={`invoice-check-${row.id}`}
          />
        )}
      </td>
      <td className="px-2 py-2 font-semibold text-slate-800 dark:text-slate-100">
        {row.contact || <span className="text-rose-600">No customer</span>}
        {/* Who the job says, when that isn't who pays. */}
        {row.customer_name && row.contact && row.customer_name !== row.contact && (
          <div className="text-[11px] font-normal text-slate-400 dark:text-slate-500">
            {row.customer_name}
          </div>
        )}
      </td>
      <td className="px-2 py-2 text-slate-500">{row.rego || "—"}</td>
      <td className="px-2 py-2 text-slate-500">{row.reference || "—"}</td>
      <td className="px-2 py-2 text-slate-500 whitespace-nowrap">
        {localDay(row.completed_at)}
      </td>
      <td className="px-2 py-2 text-right font-mono whitespace-nowrap">
        {money(row.total_ex_tax)}
      </td>
      <td className="px-2 py-2">
        {row.problems?.length > 0 && (
          <span className="inline-flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <span>{row.problems.join(" · ")}</span>
          </span>
        )}
      </td>
    </tr>
  );
}

export default function InvoicingPage() {
  const { user } = useAuth();
  const mayExport = can(user, "set_billing_state");
  const [fmt, setFmt] = useState("xero");
  const [data, setData] = useState({ ready: [], blocked: [], in_yard: [] });
  const [showInYard, setShowInYard] = useState(false);
  const [batches, setBatches] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [truckById, setTruckById] = useState({});
  const [detailJob, setDetailJob] = useState(null);
  const [sort, setSort] = useState({ key: "completed_at", dir: "desc" });

  // First click sorts; clicking the same heading again reverses it. Text
  // starts A–Z, dates and money start with the biggest/newest.
  const onSort = (key) =>
    setSort((s) => (s.key === key
      ? { key, dir: s.dir === "asc" ? "desc" : "asc" }
      : { key, dir: COLUMNS.find((c) => c.key === key)?.type === "text" ? "asc" : "desc" }));

  const readyRows = sortRows(data.ready, sort);
  const blockedRows = sortRows(data.blocked, sort);

  // The invoicing list only carries billing fields, so pull the real job
  // before showing the detail sheet — same record History opens.
  const openJob = useCallback(async (jobId) => {
    try {
      setDetailJob(await getJob(jobId));
    } catch {
      toast.error("Couldn't open that job");
    }
  }, []);

  const load = useCallback(async (which = fmt) => {
    setLoading(true);
    try {
      const [p, b, t] = await Promise.all([
        pendingInvoices(which),
        invoiceBatches(),
        listTrucks().catch(() => []),
      ]);
      setData(p);
      setBatches(b);
      setSelected(p.ready.map((r) => r.id));
      const m = {};
      t.forEach((x) => { m[x.id] = x; });
      setTruckById(m);
    } catch {
      toast.error("Couldn't load the invoicing list");
    } finally {
      setLoading(false);
    }
  }, [fmt]);

  useEffect(() => { load(fmt); }, [fmt, load]);

  const toggle = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  // Tick-all works per table: ticking every blocked row at once is how you
  // clear a batch of leftovers, and it shouldn't drag the exportable ones in.
  const toggleGroup = (rows) => {
    const ids = rows.map((r) => r.id);
    const allOn = ids.length > 0 && ids.every((id) => selected.includes(id));
    setSelected((s) => (allOn
      ? s.filter((id) => !ids.includes(id))
      : [...new Set([...s, ...ids])]));
  };

  const groupState = (rows) => {
    const on = rows.filter((r) => selected.includes(r.id)).length;
    if (!on) return false;
    return on === rows.length ? true : "indeterminate";
  };

  // Only rows in "Ready" can actually be exported — a blocked row can be
  // ticked so it can be written off or marked non-chargeable in bulk, and
  // must not slip into the file.
  const exportable = selected.filter((id) => data.ready.some((r) => r.id === id));

  const clearSelected = async (state) => {
    const label = state === "non_chargeable" ? "Non Chargeable" : "Written Off";
    if (!window.confirm(
      `Mark ${selected.length} job${selected.length === 1 ? "" : "s"} as ${label}?`
      + "\n\nThey'll leave this list and show as " + label + " in History. "
      + "You can change it back from the billing pill on any job."
    )) return;
    setBusy(true);
    try {
      const res = await setBillingStateBulk(selected, state);
      toast.success(`${res.updated} job${res.updated === 1 ? "" : "s"} marked ${label}`);
      load(fmt);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't update those jobs");
    } finally {
      setBusy(false);
    }
  };

  const exportNow = async () => {
    if (!exportable.length) { toast.error("Nothing selected that can be exported"); return; }
    setBusy(true);
    try {
      const res = await runInvoiceExport(exportable, fmt);
      saveFile(res.file_name, res.content);
      toast.success(
        `${res.batch.invoice_count} invoice${res.batch.invoice_count === 1 ? "" : "s"} exported — now marked Invoice Sent`
      );
      if (res.skipped?.length) {
        toast.warning(`${res.skipped.length} job(s) skipped — see the warnings`);
      }
      load(fmt);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Export failed");
    } finally {
      setBusy(false);
    }
  };

  const redownload = async (id) => {
    try {
      const res = await downloadInvoiceBatch(id);
      saveFile(res.file_name, res.content);
    } catch {
      toast.error("Couldn't fetch that file");
    }
  };

  const undo = async (id) => {
    if (!window.confirm(
      "Put these invoices back to To Invoice?\n\nUse this if the import failed at the "
      + "accounting end. The invoice numbers already used won't be reused."
    )) return;
    try {
      const res = await undoInvoiceBatch(id);
      toast.success(`${res.reverted} job(s) back on the To Invoice list`);
      load(fmt);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't undo that export");
    }
  };

  const selectedTotal = data.ready
    .filter((r) => exportable.includes(r.id))
    .reduce((sum, r) => sum + (r.total_ex_tax || 0), 0);

  return (
    <div className="p-6 space-y-6" data-testid="invoicing-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-black text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <FileSpreadsheet className="h-6 w-6 text-blue-600" /> Invoicing
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Everything waiting to be invoiced. Export a file, import it into your
            accounting system, and these jobs move to Invoice Sent.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={() => load(fmt)}
          data-testid="invoicing-refresh"
        >
          <RefreshCw className="h-4 w-4" /> Refresh
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="invoicing-format-picker">
        {FORMATS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFmt(f.key)}
            data-testid={`invoicing-format-${f.key}`}
            className={`text-left px-3 py-2 rounded-md border transition-colors ${
              fmt === f.key
                ? "border-blue-600 bg-blue-50 dark:bg-blue-950/40"
                : "border-slate-200 dark:border-slate-700 hover:border-slate-400"
            }`}
          >
            <div className="text-sm font-bold text-slate-800 dark:text-slate-100">{f.label}</div>
            <div className="text-xs text-slate-500">{f.hint}</div>
          </button>
        ))}
      </div>

      {fmt === "myob" && (
        <div
          className="text-sm border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30 rounded-md p-3 text-amber-900 dark:text-amber-200"
          data-testid="invoicing-myob-warning"
        >
          <strong>AccountRight only.</strong> MYOB Business — the cloud version — can't
          import invoices at all; MYOB themselves point people at their API for that. If
          you're on MYOB Business this file won't help, and we'd need to connect to your
          account instead.
        </div>
      )}

      {data.settings_ready === false && (
        <div
          className="text-sm border border-blue-300 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30 rounded-md p-3 text-blue-900 dark:text-blue-200"
          data-testid="invoicing-settings-needed"
        >
          <strong>One-off setup needed.</strong> Nothing can be exported until your
          tax rate name and account codes are entered — otherwise the invoices would
          import with no GST on them.{" "}
          <Link to="/settings" className="underline font-semibold" data-testid="invoicing-settings-link">
            Open Settings → Invoice Export
          </Link>
        </div>
      )}

      <section className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md">
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-slate-200 dark:border-slate-700">
          <div className="text-sm font-bold text-slate-700 dark:text-slate-200">
            Ready to invoice
            <span className="ml-2 text-slate-400 font-normal" data-testid="invoicing-ready-count">
              {data.ready.length} job{data.ready.length === 1 ? "" : "s"}
            </span>
          </div>
          <div className="flex items-center gap-3">
            {mayExport && (
              <span className="text-sm text-slate-500" data-testid="invoicing-selected-total">
                {exportable.length} selected · {money(selectedTotal)} ex GST
              </span>
            )}
            {mayExport && (
              <Button
                type="button"
                onClick={exportNow}
                disabled={busy || !exportable.length}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="invoicing-export-btn"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                Export {FORMATS.find((f) => f.key === fmt).label} file
              </Button>
            )}
          </div>
        </div>
        {/* Clearing the leftovers: waived storage, abandoned small balances.
            Acts on everything ticked, including the blocked rows below. */}
        {mayExport && selected.length > 0 && (
          <div
            className="flex flex-wrap items-center gap-2 px-4 py-2.5 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40"
            data-testid="invoicing-bulk-bar"
          >
            <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">
              {selected.length} ticked — not getting invoiced?
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() => clearSelected("non_chargeable")}
              data-testid="invoicing-mark-non-chargeable"
            >
              <Ban className="h-3.5 w-3.5" /> Not chargeable
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() => clearSelected("written_off")}
              data-testid="invoicing-mark-written-off"
            >
              <CircleSlash className="h-3.5 w-3.5" /> Write off
            </Button>
            <button
              type="button"
              onClick={() => setSelected([])}
              className="text-xs text-slate-500 underline ml-auto"
              data-testid="invoicing-clear-selection"
            >
              Clear selection
            </button>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500">
              <SortableHeaders
                sort={sort}
                onSort={onSort}
                lastLabel=""
                master={mayExport && readyRows.length > 0 && (
                  <Checkbox
                    checked={groupState(readyRows)}
                    onCheckedChange={() => toggleGroup(readyRows)}
                    className="h-5 w-5"
                    title="Tick or clear every job here"
                    data-testid="invoicing-check-all-ready"
                  />
                )}
              />
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="px-4 py-6 text-center text-slate-400">
                  <Loader2 className="h-4 w-4 animate-spin inline" /> Loading…
                </td></tr>
              )}
              {!loading && data.ready.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500" data-testid="invoicing-empty">
                  {data.blocked.length > 0 ? (
                    <>
                      <AlertTriangle className="h-5 w-5 inline text-amber-500" /> Nothing ready
                      to export yet — the {data.blocked.length} job
                      {data.blocked.length === 1 ? "" : "s"} below need something fixed first.
                    </>
                  ) : (
                    <>
                      <Check className="h-5 w-5 inline text-emerald-600" /> Nothing waiting — everything's invoiced.
                    </>
                  )}
                </td></tr>
              )}
              {readyRows.map((r) => (
                <JobRow
                  key={r.id}
                  row={r}
                  checked={selected.includes(r.id)}
                  onToggle={toggle}
                  selectable={mayExport}
                  onOpen={openJob}
                />
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {data.blocked.length > 0 && (
        <section
          className="bg-white dark:bg-slate-900 border border-amber-300 dark:border-amber-800 rounded-md"
          data-testid="invoicing-blocked"
        >
          <div className="px-4 py-3 border-b border-amber-200 dark:border-amber-800">
            <div className="text-sm font-bold text-amber-800 dark:text-amber-300">
              Needs attention first — {data.blocked.length} job{data.blocked.length === 1 ? "" : "s"}
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              These would import wrongly or not at all. Fix the cause and they'll move up.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500">
                <SortableHeaders
                  sort={sort}
                  onSort={onSort}
                  lastLabel="What's stopping it"
                  master={mayExport && blockedRows.length > 0 && (
                    <Checkbox
                      checked={groupState(blockedRows)}
                      onCheckedChange={() => toggleGroup(blockedRows)}
                      className="h-5 w-5"
                      title="Tick or clear every job here"
                      data-testid="invoicing-check-all-blocked"
                    />
                  )}
                />
              </thead>
              <tbody>
                {blockedRows.map((r) => (
                  <JobRow
                    key={r.id}
                    row={r}
                    checked={selected.includes(r.id)}
                    onToggle={toggle}
                    selectable={mayExport}
                    onOpen={openJob}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {data.in_yard?.length > 0 && (
        <section
          className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md"
          data-testid="invoicing-in-yard"
        >
          <button
            type="button"
            onClick={() => setShowInYard((v) => !v)}
            className="w-full flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors"
            data-testid="invoicing-in-yard-toggle"
          >
            <div className="flex items-center gap-2">
              <Warehouse className="h-4 w-4 text-slate-400" />
              <span className="text-sm font-bold text-slate-700 dark:text-slate-200">
                Still in the yard — {data.in_yard.length} vehicle
                {data.in_yard.length === 1 ? "" : "s"}
              </span>
              <span className="text-xs text-slate-500">
                can't be invoiced until they leave
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <span data-testid="invoicing-in-yard-total">
                {money(data.in_yard_total)} and counting
              </span>
              <ChevronDown className={`h-4 w-4 transition-transform ${showInYard ? "rotate-180" : ""}`} />
            </div>
          </button>
          {showInYard && (
            <ul className="border-t border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-800">
              {sortRows(data.in_yard, sort).map((r) => (
                <li
                  key={r.id}
                  className="px-4 py-2.5 flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-3 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors"
                  onClick={() => openJob(r.id)}
                  title="Open the full job details"
                  data-testid={`invoice-in-yard-${r.id}`}
                >
                  <span className="font-semibold text-slate-800 dark:text-slate-100 sm:flex-1 sm:truncate">
                    {r.contact || "—"}
                  </span>
                  <span className="text-xs text-slate-500 sm:w-24 sm:shrink-0">{r.rego || "—"}</span>
                  <span className="font-mono text-sm sm:w-28 sm:text-right sm:shrink-0">
                    {money(r.total_ex_tax)}
                  </span>
                  <span className="text-xs text-slate-500 sm:w-[22rem] sm:shrink-0">
                    {r.in_yard_reason}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      <section
        className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md"
        data-testid="invoicing-batches"
      >
        <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 text-sm font-bold text-slate-700 dark:text-slate-200">
          Past exports
        </div>
        {batches.length === 0 ? (
          <div className="px-4 py-6 text-sm text-slate-500">No exports yet.</div>
        ) : (
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {batches.map((b) => (
              <li key={b.id} className="px-4 py-3 flex flex-wrap items-center justify-between gap-3" data-testid={`invoice-batch-${b.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                    {b.invoice_count} invoice{b.invoice_count === 1 ? "" : "s"} · {money(b.total_ex_tax)} ex GST
                    <span className="ml-2 text-xs uppercase font-bold text-slate-400">{b.format}</span>
                    {b.undone_at && (
                      <span className="ml-2 text-xs font-bold text-rose-600" data-testid={`batch-undone-${b.id}`}>
                        UNDONE
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 break-all">
                    {(b.created_at || "").slice(0, 16).replace("T", " ")} · {b.created_by} · {b.file_name}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button type="button" variant="outline" size="sm" onClick={() => redownload(b.id)} data-testid={`batch-download-${b.id}`}>
                    <Download className="h-3.5 w-3.5" /> File
                  </Button>
                  {mayExport && !b.undone_at && (
                    <Button type="button" variant="outline" size="sm" onClick={() => undo(b.id)} data-testid={`batch-undo-${b.id}`}>
                      <Undo2 className="h-3.5 w-3.5" /> Undo
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <JobDetailSheet
        job={detailJob}
        open={!!detailJob}
        onOpenChange={(o) => !o && setDetailJob(null)}
        truckById={truckById}
      />
    </div>
  );
}
